import i18next from 'i18next';
import LocaleKeys from '../locales/keys';
import moment from 'moment';
import * as currencyFormatter from 'currency-format';
import CONSTANTS from '../constants/externalReferences';
import { UserSession } from 'ascendon-web-toolkit/utils';

import visaIcon from '../images/visa.svg';
import googlePayIcon from '../images/google_pay_icon.svg';
import applePayIcon from '../images/apple_pay_icon.svg';
import mastercardIcon from '../images/mastercard.svg';
import amexIcon from '../images/amex.svg';
import amexIconStripe from '../images/amex-stripe.svg';
import masterCardIconStripe from '../images/mastercard-stripe.svg';
import dinersIcon from '../images/diners.svg';
import discoverIcon from '../images/discover.svg';
import jcbIcon from '../images/jcb.svg';
import maestroIcon from '../images/maestro.svg';
import defaultCardIcon from '../images/defaultcard.svg';

import DE from '../images/free-trial/DE.png';
import EN from '../images/free-trial/EN.png';
import ES from '../images/free-trial/ES.png';
import FR from '../images/free-trial/FR.png';
import NL from '../images/free-trial/NL.png';
import PT from '../images/free-trial/PT.png';
import postMessage from './postMessage';
import { ensureWeHaveTwoLetterPlaceHolders } from '../common/helpers/maskPlaceHolderConfiguration';

/**
 * Reads session from localStorage
 *
 * @return {string}      user session
 */
export function readSession() {
  return UserSession?.sessionData || '';
}

export function readSessionId() {
  return readSession().SessionId || '';
}

export function readSubscriberId() {
  return readSession()?.SessionSummary?.SubscriberId || null;
}

export function readSubscriberCountry() {
  return (
    readSession()?.Country ||
    readSession()?.HomeCountry ||
    readSession()?.Subscriber?.Country ||
    readSession()?.Subscriber?.HomeCountry ||
    null
  );
}

export function setSession(sessionObj) {
  if (sessionObj?.SessionId) {
    UserSession.setSessionData(sessionObj);
  } else {
    // remove storage
    UserSession.remove();
  }
}

export function removeSession() {
  UserSession.remove();
  localStorage.removeItem('redirectURL');
}

export function getUserEmail() {
  return readSession()?.SessionSummary?.Email || '';
}

export const getUserName = () => {
  const getUserNameBasedOnSession = (session) => {
    const { SessionSummary, Subscriber } = session;
    if (SessionSummary || Subscriber) {
      const { FirstName, LastName } = SessionSummary || Subscriber;
      return `${FirstName || ''} ${LastName || ''}`;
    }
    return '';
  };
  return getUserNameBasedOnSession(readSession());
};

/**
 * Get Type of Credit Card
 *
 * @param  {string} number card number
 *
 * @return {object}      card type value
 */
export const getCreditCardType = (_number) => {
  const validationRegex = {
    Visa: {
      numberRegex: /^4[0-9]{15}$/,
      cvvRegex: /^[0-9]{3}$/,
    },
    MasterCard: {
      numberRegex:
        /(^5[1-5][0-9]{14}$)|((^222[1-9]|^22[3-9][0-9]|^2[3-6][0-9][0-9]|^27[0-1][0-9]|^2720)[0-9]{12}$)/,
      cvvRegex: /^[0-9]{3}$/,
    },
  };
  const number = _number.replace(/-/g, '');

  if (number.match(validationRegex.Visa.numberRegex) != null)
    return { Type: 1, number };

  if (number.match(validationRegex.MasterCard.numberRegex) != null)
    return { Type: 2, number };

  return { Type: 0, number };
};

export const creditCardType = {
  Visa: 1,
  MasterCard: 2,
  AmericanExpress: 3,
  Discover: 4,
  JCB: 5,
  DinersClub: 6,
  MaestroUK: 7,
  MaestroInternational: 8,
  Solo: 9,
  VisaElectron: 15,
  Laser: 17,
  BrandedVisa: 21,
  BrandedMastercard: 22,
};

export const paymentInstrumentTypes = {
  creditCard: 0,
  eCheck: 1,
  storedValueAccount: 2,
  giftCard: 3,
  externalBill: 4,
  paypalAccount: 5,
  iTunesAccount: 6,
  directDebit: 8,
  xboxAccount: 9,
  externalGiftCard: 10,
  rokuAccount: 11,
};

const imageMap = {
  visaIcon,
  mastercardIcon,
  amexIcon,
  dinersIcon,
  discoverIcon,
  jcbIcon,
  maestroIcon,
  defaultCardIcon,
};
const getCard = (cardName) => imageMap[cardName] || imageMap['defaultCardIcon'];

export const stripeCardBrandImageUrls = (brandName) => {
  const brandConfig = {
    visa: () => visaIcon,
    mastercard: () => masterCardIconStripe,
    amex: () => amexIconStripe,
    diners: () => dinersIcon,
    discover: () => discoverIcon,
    jcb: () => jcbIcon,
    maestroIcon: () => maestroIcon,
    default: () => defaultCardIcon,
  };
  return (brandConfig[brandName] || brandConfig['default'])();
};

export const stripeWalletBrandImageUrls = (name) => {
  const brandConfig = {
    google_pay: () => googlePayIcon,
    apple_pay: () => applePayIcon,
    default: () => defaultCardIcon,
  };
  return (brandConfig[name] || brandConfig['default'])();
};

export const getCreditCardIconByType = (cardType) => {
  switch (parseInt(cardType)) {
    case creditCardType.Visa:
    case creditCardType.VisaElectron:
    case creditCardType.BrandedVisa:
      return getCard('visaIcon');
    case creditCardType.MasterCard:
    case creditCardType.BrandedMastercard:
      return getCard('mastercardIcon');
    case creditCardType.AmericanExpress:
      return getCard('amexIcon');
    case creditCardType.DinersClub:
      return getCard('dinersIcon');
    case creditCardType.Discover:
      return getCard('discoverIcon');
    case creditCardType.JCB:
      return getCard('jcbIcon');
    case creditCardType.MaestroInternational:
    case creditCardType.MaestroUK:
      return getCard('maestroIcon');
    default:
      return getCard('defaultCardIcon');
  }
};

export const getFreeTrialImage = () => {
  const imageMap = {
    DE,
    EN,
    ES,
    FR,
    NL,
    PT,
  };

  const imageKey = i18next.t(LocaleKeys.freeTrial.image);
  const image = imageMap[imageKey];
  return image;
};

export const getSelectedCreditCardName = (
  cardType,
  cardList = creditCardType,
) => {
  const getCardName = (Object.entries(cardList) || []).reduce((all, item) => {
    const [key, value] = item;
    if (value === cardType) {
      all = { cardName: key, cardType: value };
    }
    return all;
  }, {});
  return getCardName;
};

export const generateCreditCardDescriptor = (creditCardNumber) => {
  const getCreditCardDetails = getCreditCardType(creditCardNumber);
  const cardName = getCreditCardDetails.display;
  const lastFour = creditCardNumber.substring(creditCardNumber.length - 4);
  const endingText = i18next.t(LocaleKeys.common.creditCardDescriptor);
  return `${cardName} ${endingText} ${lastFour}`;
};

const currencyFix = (input) => {
  // Fix for change in underlying JSON currency info.  See https://github.com/xsolla/currency-format/pull/25/files.
  if (input === 'US$') {
    return input.replace('US', '');
  }
  return input;
};

const getPriceWithCurrencySymbol = (
  prefixCurrencySymbol,
  currencySymbol,
  price,
) => {
  if (!prefixCurrencySymbol) {
    return price.concat(' ', currencySymbol);
  }
  return currencySymbol.concat(price);
};

export const getCurrencyDetails = (value, currencyCode) => {
  value = value || 0;
  const currentCurrency =
    currencyFormatter[currencyCode] || currencyFormatter.USD;

  const prefixCurrencySymbol =
    currentCurrency.symbol.template.indexOf('$') <
    currentCurrency.symbol.template.indexOf('1');

  const currencySymbol = currencyFix(currentCurrency.symbol.grapheme);

  const price = `${value?.toFixed(currentCurrency.fractionSize)}`;

  const priceWithCurrencySymbol = getPriceWithCurrencySymbol(
    prefixCurrencySymbol,
    currencySymbol,
    price,
  );

  return {
    currentCurrency,
    prefixCurrencySymbol,
    currencySymbol,
    price,
    priceWithCurrencySymbol,
  };
};

export const getValidCountriesList = (countriesResponse) => {
  const candidate = countriesResponse?.data?.Codes?.filter(
    (countryItem) => countryItem.Value !== 'UNK',
  );
  return candidate || {};
};

export const getIapPaymentInstrumentType = (
  paymentInstrumentType,
  paymentInstrumentTypeList = [13, 11, 12, 6, 4],
) => paymentInstrumentTypeList.includes(paymentInstrumentType);

const getLocale = () => {
  let locale = window.navigator.userLanguage || window.navigator.language;
  locale = locale.replace(/-hans/gi, '').replace(/-hant/gi, '');
  return locale;
};

//get locale specific date formats
export const getLocalDateFormat = () => {
  const locale = getLocale();
  moment.locale(locale);
  const localeData = moment.localeData();
  let candidate = localeData.longDateFormat('L');
  const replaceTextDirectionMarker = (input) => input.replace(/\u200f/g, '');

  candidate = replaceTextDirectionMarker(candidate);
  candidate = ensureWeHaveTwoLetterPlaceHolders(candidate);
  return candidate;
};

//get locale specific long dates(ex: 6 April 2022)
export const getLocaleLongDate = (stringDate) => {
  const date = new Date(stringDate);
  const locale = getLocale();
  return moment(date).locale(locale).format('LL');
};

const isValidDate = (date) => {
  if (Object.prototype.toString.call(date) !== '[object Date]') {
    return false;
  }
  return !isNaN(date.getTime());
};

//convert the ISO date string(received from api) to locale specific date format
export const formatDateString = (dateString) => {
  if (!dateString) return undefined;
  const replaceTextDirectionMarker = (input) => input.replace(/\u200f/g, '');

  const tempDate = new Date(dateString.replace(/-/g, '/').replace(/T.+/, ''));
  const localDateFormat = getLocalDateFormat();
  const newOutput = replaceTextDirectionMarker(
    moment(tempDate).locale('en-US').format(localDateFormat),
  );

  if (isValidDate(tempDate)) {
    return newOutput;
  } else {
    return undefined;
  }
};

//create date formats which satisfy ISO date format(YYYY-MM-DD) with an ISO Timestamp(Ex: '1995-12-31T00:00:00.000Z')
export const createDateFormat = (inputDateFormat) => {
  const internalModelFormat = 'YYYY-MM-DD';
  return {
    formatForSubscriberAPI: (value) => {
      if (value) {
        return new Date(value).toISOString();
      }
      return undefined;
    },
    parse: (value) => {
      if (value) {
        const candidate = moment(value, inputDateFormat).locale('en-US');
        if (candidate.isValid()) {
          return candidate.format(internalModelFormat);
        }
        return undefined;
      }
      return undefined;
    },
  };
};
//should be altered with locales wrt ASCPHX-1980
export const getLocaleDate = (date, format) =>
  moment(date).utc().format(format);

export const fixCountryCodePageIssueIfRequired = (country) => {
  if (country) {
    let candidate = country.Name;
    if (country.Value === 'CIV') {
      candidate = country.Name.replace('\u0092', '\u2019');
    }
    return candidate;
  }
};

export const headerConfig = {
  sessionId: 'CD-SessionId',
  cacheControl: 'cache-control',
  contentType: 'Content-Type',
  systemIdHeader: 'CD-SystemId',
  languageHeader: 'CD-Language',
};

export const setHeadersConfig = (type, configSettings = {}) => {
  const { systemId, language } = configSettings;
  const headerConfigNode = {
    'CD-SessionId': () => readSessionId(),
    'cache-control': () => 'no-cache',
    'Content-Type': () => 'application/json',
    'CD-SystemId': () => systemId,
    'CD-Language': () => language,
    default: () => '',
  };
  return (headerConfigNode[type] || headerConfigNode['default'])();
};

const convertLeadSourceToValue = (leadSource) => {
  if (leadSource && leadSource.Value) {
    return parseInt(leadSource.Value, 10);
  }
  return null;
};

const findLeadSource = (leadSourceList, leadSourceName) => {
  if (leadSourceList && leadSourceName) {
    const candidateLeadSourceName = leadSourceName.toLowerCase();
    return leadSourceList.filter(
      (individualLeadSourceType) =>
        individualLeadSourceType.Name.toLowerCase() === candidateLeadSourceName,
    )[0];
  }
  return null;
};
export const getLeadSourceValue = (leadSourceCodes = []) => {
  const leadSourceFromSessionStorage = sessionStorage.getItem(
    CONSTANTS.LEAD_SOURCE,
  );
  if (leadSourceFromSessionStorage) {
    const leadSourceMetadata = findLeadSource(
      leadSourceCodes,
      leadSourceFromSessionStorage,
    );
    if (leadSourceMetadata?.Value) {
      return convertLeadSourceToValue(leadSourceMetadata);
    } else {
      const invalidLeadSourceMetadata = findLeadSource(
        leadSourceCodes,
        CONSTANTS.INVALID,
      );
      return convertLeadSourceToValue(invalidLeadSourceMetadata);
    }
  } else {
    const defaultLeadSourceMetadata = findLeadSource(
      leadSourceCodes,
      CONSTANTS.WEB_ACCOUNT,
    );
    return convertLeadSourceToValue(defaultLeadSourceMetadata);
  }
};

export const isSessionOrientedMessageEvent = (messageNode) => {
  if (messageNode?.page === 'subscriber.loggedIn') {
    const params = messageNode?.params;
    return { isSessionMessage: true, createSessionResponse: params };
  }
  if (messageNode?.page === 'subscriber.loggedOut') {
    return { isSessionMessage: true, isLogout: true };
  }
  return { isSessionMessage: false };
};

export const sessionAndLogoutEventHandler = (sessionMessage, routeHistory) => {
  if (sessionMessage?.isSessionMessage) {
    if (sessionMessage?.createSessionResponse) {
      const { createSessionResponse } = sessionMessage;
      UserSession.setSessionData(createSessionResponse);
    }
    if (sessionMessage?.isLogout) {
      removeSession();
      routeHistory.push('/');
    }
  }
};

export const scrollOptions = {
  id: 'ascendon',
  behavior: 'smooth',
};

export const scrollToTop = (top = 0, left = 0, behavior = 'instant') => {
  postMessage('module.scrollTo', {
    top,
    left,
    behavior,
  });
};

export const postMessageQuickCancelAnalyticsEvent = (
  isQuickCancel,
  eventName,
  params,
) => {
  if (!isQuickCancel) {
    return;
  }
  postMessage(eventName, params);
};
